/**
 * What an import did, row by row — the shape `POST /{resource}/import` answers.
 *
 * Every row is attempted and the failures are listed rather than the whole file
 * being refused, so one bad row in a hundred is one correction rather than a
 * hundred re-uploads.
 */

export interface MasterImportRejection {
  /** The line as the person's editor counts it: the header is line 1. */
  line: number;
  /** The row's identifying value where one could be read, for finding it again. */
  code: string | null;
  reason: string;
}

/**
 * A row that DID import, with something about it worth saying.
 *
 * The third outcome, beside imported and rejected. A value written with surrounding spaces is trimmed on
 * write, as every master has always done — so the file and the table disagree about what was uploaded.
 * Rejecting the row would fail a hundred-row file over a typing accident, and keeping the spaces would make
 * ` Sales` and `Sales` two business units that look identical in every list. Saying so is the only one of
 * the three the uploader can act on.
 */
export interface MasterImportWarning {
  /** The line as the person's editor counts it: the header is line 1. */
  line: number;
  code: string | null;
  /** What was adjusted, naming the columns. */
  detail: string;
}

export interface MasterImportResult {
  /** Data rows found, excluding the header and blank lines. */
  read: number;
  imported: number;
  rejected: MasterImportRejection[];
  /**
   * Rows that imported with an adjustment worth reporting. Optional: an endpoint that has nothing to say
   * omits it, and a response from before warnings existed carries none.
   */
  warnings?: MasterImportWarning[];
  /**
   * The refused rows as an `.xlsx` workbook, Base64 encoded — the uploaded file's own columns with an
   * Error Description beside each row that failed.
   *
   * It arrives WITH the answer rather than from an endpoint of its own because a synchronous import keeps
   * nothing: once the upload has been answered, the file and its failures are gone, so there would be
   * nothing left to ask for. A queued import stores both and serves its report from
   * `/import-jobs/{id}/errors` instead — which is why `MasterImportJob` has no field like this.
   *
   * Optional, and absent on every master that does not build one: the popup's own list of rejections is
   * what those still show.
   */
  errorReport?: string | null;
  /** What to save that workbook as; absent alongside an absent report. */
  errorReportName?: string | null;
}

/** Where a queued import has got to. Mirrors the server's `MasterImportJobStatus`. */
export type MasterImportJobStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

/**
 * A queued import — what `POST /{resource}/import` answers for a master whose import runs in the
 * background, in place of a `MasterImportResult`.
 *
 * The counts are counts, not lists: `rejected` is how many rows were refused, and the rows themselves are
 * the downloadable error report rather than a list in the response. That is the difference the dialog has
 * to notice, and the one that broke it — `result.rejected.map` on a number.
 */
export interface MasterImportJob {
  jobId: number;
  master: string;
  fileName: string;
  status: MasterImportJobStatus;
  /** Data rows found in the file; settled when it is parsed, on upload. */
  read: number;
  /** How far the worker has got, for a progress bar. */
  processed: number;
  imported: number;
  rejected: number;
  /**
   * The size the server is running this file in, so the tray can say "Batch 3 of 11".
   *
   * Carried rather than assumed: it is a server property, and a screen quoting a batch count the server is
   * not using would be confidently wrong.
   */
  batchSize: number;
  /** A line fit to show: what is happening, or what went wrong. */
  message: string;
  /**
   * The first few rejected rows, so a small file explains itself on screen.
   *
   * Capped by the server: past that the count stands and the error report is the answer. Optional, because
   * a job that has rejected nothing carries none.
   */
  rejections?: MasterImportRejection[];
}

/** What an import endpoint answers: the whole result at once, or a job that will produce one. */
export type MasterImportOutcome = MasterImportResult | MasterImportJob;

/**
 * Whether this outcome is a queued job rather than a finished result.
 *
 * Keyed on `jobId` rather than on the shape of `rejected`: a number and an array are both truthy, and
 * `Array.isArray` on a field that should not be there at all reads as a workaround rather than a
 * discriminator. Seventeen masters still answer the result shape, so both are live.
 */
export function isImportJob(outcome: MasterImportOutcome): outcome is MasterImportJob {
  return typeof (outcome as MasterImportJob).jobId === 'number';
}

/** Whether a job has stopped moving, either way. */
export function isImportJobFinished(job: MasterImportJob): boolean {
  return job.status === 'COMPLETED' || job.status === 'FAILED';
}
